import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './BottomNav.css';
import {
    IoBarChart, IoBarChartOutline,
    IoCreate, IoCreateOutline,
    IoGrid, IoGridOutline,
    IoGolf, IoGolfOutline,
    IoSettings, IoSettingsOutline,
} from 'react-icons/io5';
import { useAuth } from './AuthContext';
import { preloadRoute, preloadRouteWhenIdle } from './routePreload';

const ICON_SIZE = 24;
// A safety net, not a size anyone sees: a label that fits at 100% text needs
// at most 1/textZoom, so the fit never takes a larger-text reader below 12px.
const LABEL_MIN_SCALE = 0.5;

// THE BAR OWNS ITS GLYPHS, keyed by tab. A dozen screens used to pass their
// own menuItems and they had drifted into three icon families — Feather
// outlines at one weight, a solid Game Icons tee beside them — so the same bar
// looked different from page to page and never matched itself. Ionicons draws
// every tab as an OUTLINE/FILLED pair at one stroke (32 of 512), which is how a
// native tab bar marks the tab you are on: the shape fills, not just the tint.
// A tab whose label is not here keeps the icon its page passed.
const TAB_GLYPHS = {
    play: [IoGolfOutline, IoGolf],
    scoring: [IoCreateOutline, IoCreate],
    leaderboard: [IoBarChartOutline, IoBarChart],
    rounds: [IoGridOutline, IoGrid],
    manage: [IoSettingsOutline, IoSettings],
};

/*
 * ONE BAR, FOUR TABS, EVERYWHERE: Play · Leaderboard · Rounds · Manage.
 *
 * Twenty-three screens used to pass their own `menuItems`, which produced six
 * different tab sets: Rounds moved slot from page to page, Home was not a tab
 * at all, and tapping Play Golf landed on a page whose bar had no Play Golf in
 * it. The bar now owns the set, the way it already owned the glyphs. Manage
 * is the setup menu organisers open every week, so it stays one tap away (it
 * was briefly a gear on Home; that was a demotion it cannot take). HOME IS
 * NOT A TAB: a fifth tab left "Leaderboard" 70px for 75px of word, so it
 * wrapped on every phone. Home is the first item in the hamburger, which every
 * page this bar leads to carries (Play, Leaderboard, Rounds, Manage).
 *
 * `menuItems` is still ACCEPTED, so no screen had to change at once, but only
 * as HINTS: which tab it marks `active`, and a leaderboard link already scoped
 * to a competition (`?tournament=…`), which is kept so the board opens on the
 * round the player was looking at. Its tab set is ignored.
 *
 * Play becomes SCORING, with a live dot, only on the Leaderboard and Rounds
 * pages while the competition selected there is live (`live: true` on the
 * page's Enter Score item) — see `pageScoring` below.
 */
const HINT_TAB = {
    'Play Golf': 'play',
    'Enter Scores': 'play',
    'Enter Score': 'play',
    'Leaderboard': 'leaderboard',
    'Rounds': 'rounds',
    'Manage': 'manage',
};

const tabForPath = (pathname) => {
    if (pathname === '/play' || pathname.startsWith('/live-scoring/')
        || pathname.startsWith('/team-scoring/') || pathname.endsWith('/scorecard-input')
        || pathname.startsWith('/tournaments/add')) return 'play';
    if (pathname.startsWith('/leaderboard')) return 'leaderboard';
    if (pathname === '/tournaments/scorecard-entry') return 'rounds';
    if (pathname === '/setup') return 'manage';
    return null;
};

// A native tab bar marks the tab you are on by filling its shape, not just
// tinting it: every tab is an outline/filled pair at one stroke.
const renderGlyph = (item, isActive) => {
    const Glyph = TAB_GLYPHS[item.glyph || item.key][isActive ? 1 : 0];
    return <Glyph size={ICON_SIZE} aria-hidden focusable={false} />;
};

const BottomNav = ({ menuItems, active }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const rowRef = useRef(null);
    // The tab the user just pressed, lit immediately — see below.
    const [pressed, setPressed] = useState(null);
    const { userProfile, isViewingAsAdmin } = useAuth();
    // An open admin view is a society to navigate, even though it is not one of
    // this user's memberships — without this the whole bar sat disabled for a
    // system admin supporting someone else's society.
    const hasSocieties = userProfile?.societies?.length > 0 || isViewingAsAdmin;

    // SCORING COMES FROM THE PAGE, NEVER FROM A STATUS FETCH. Only the
    // Leaderboard and Rounds pages, with a live competition selected, turn Play
    // into Scoring — using that page's own action for that competition. It
    // used to follow /me/status too, which is fetched and cached on its own
    // clock: the tab flipped late, flipped on screens unrelated to the round,
    // and missed organisers who were not playing.
    const pageScoring = (menuItems || []).find(
        (i) => (i.name === 'Enter Score' || i.name === 'Enter Scores') && i.live);

    const hinted = (menuItems || []).find((i) => i.active === true);
    const scopedBoard = (menuItems || []).find(
        (i) => i.name === 'Leaderboard' && typeof i.route === 'string' && i.route.includes('?'));
    const activeTab = active !== undefined ? active
        : hinted ? (HINT_TAB[hinted.name] || null)
            : tabForPath(location.pathname);

    const itemsToRender = [
        pageScoring
            ? { key: 'play', glyph: 'scoring', name: 'Scoring', route: pageScoring.route, state: pageScoring.state, onClick: pageScoring.onClick, live: true, needsSociety: true }
            : { key: 'play', name: 'Play', route: '/play', needsSociety: true },
        { key: 'leaderboard', name: 'Leaderboard', route: scopedBoard ? scopedBoard.route : '/leaderboard', needsSociety: true },
        { key: 'rounds', name: 'Rounds', route: '/tournaments/scorecard-entry', needsSociety: true },
        { key: 'manage', name: 'Manage', route: '/setup' },
    ];

    // --- THE TAP ANSWERS IN THE SAME FRAME ----------------------------------
    // Every screen behind this bar is a lazy chunk, so between the tap and the
    // new screen there is a JavaScript download — short, but long enough that
    // a bar which does not visibly react reads as a bar that did not hear you,
    // and people press it again. The tapped tab takes the active treatment
    // straight away and holds it until the route catches up.
    //
    // It is dropped the moment the location changes, so the real `active`
    // takes over and nothing lingers if the navigation goes somewhere else.
    useEffect(() => { setPressed(null); }, [location.pathname]);


    // A backstop, because not every tab navigates: a few screens hand this bar
    // their own `onClick`, and one that decides it has nowhere to go would
    // otherwise leave a tab lit for the rest of the session.
    useEffect(() => {
        if (pressed == null) return undefined;
        const t = setTimeout(() => setPressed(null), 1500);
        return () => clearTimeout(t);
    }, [pressed]);

    // And warm the chunk itself, so there is usually nothing to wait for.
    // On a quiet moment, for the screen a scorer is most likely to want next.
    useEffect(() => {
        preloadRouteWhenIdle('/tournaments/0/scorecard-input');
        // Only on arrival: this is speculative work and must never repeat on
        // every render of a bar that is on every screen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- The bar publishes its own height -----------------------------------
    // Every page that keeps its last row clear of the bar reads
    // --bottom-nav-height (or --bottom-nav-total). That number used to be a
    // constant, and the bar is not a constant height: the labels never wrap
    // (see below), but a system text-size setting still makes the line taller,
    // and a page that reserved the stylesheet's figure would hide a row under it.
    //
    // Measured on the ROW, which excludes the safe-area padding, because the
    // token's contract is "the bar above the home indicator" — --bottom-nav-total
    // adds the inset back and several pages add it themselves. The border is
    // added because it is part of what covers the page.
    //
    // No loop: nothing in this bar's own layout reads the token it publishes.
    useEffect(() => {
        const row = rowRef.current;
        if (!row) return undefined;
        const publish = () => {
            const border = parseFloat(
                getComputedStyle(row.parentElement).borderTopWidth,
            ) || 0;
            document.documentElement.style.setProperty(
                '--bottom-nav-height', `${Math.round(row.offsetHeight + border)}px`,
            );
        };
        // Published once whatever happens, and kept up to date where the
        // browser can tell us. The one-shot must not depend on the observer:
        // without it no page would reserve anything but the stylesheet's
        // starting value, which is the one case this measuring exists to avoid.
        publish();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(publish);
        ro.observe(row);
        // Left in place on unmount rather than cleared: the next screen mounts
        // its own bar a frame later, and clearing would drop every page that
        // reserves this back to the stylesheet's starting value for that frame
        // — a visible jump on every navigation.
        return () => ro.disconnect();
    }, []);

    // --- The labels never wrap: they shrink, together, to fit ---------------
    // The stylesheet sizes a label at 12px, which fits "Leaderboard" with room
    // to spare on every phone — at 100% text size. Android's WebView multiplies
    // every font size by the system font-size setting, and iOS's does not, so
    // on an Android phone set to a larger font the word outgrew its column and
    // broke over two lines while the same build was fine on an iPhone. No CSS
    // size can be chosen against that, because the CSS is what gets multiplied.
    // So the label is `nowrap` and the bar measures it as RENDERED — after any
    // system scaling — and scales every label by the same factor until the
    // widest one fits. One factor for all four, or the bar reads as four sizes.
    //
    // Measured at the ACTIVE weight on every label (`.is-fitting`), because the
    // active tab is bolder and therefore wider: fitting to the current tab
    // alone would change the size of every label as the user moved between
    // pages. A layout effect, so the corrected size is what first paints.
    const labelKey = itemsToRender.map((i) => i.name).join('|');
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) return undefined;
        const fit = () => {
            row.style.setProperty('--nav-label-scale', '1');
            row.classList.add('is-fitting');
            let scale = 1;
            row.querySelectorAll('.bottom-nav-label').forEach((label) => {
                // jsdom, or a bar not laid out yet: nothing to measure.
                if (!label.clientWidth || !label.scrollWidth) return;
                scale = Math.min(scale, label.clientWidth / label.scrollWidth);
            });
            row.classList.remove('is-fitting');
            // Never cut: no ellipsis, no wrap. The floor only stops a broken
            // measurement (a zero-width column mid-layout) shrinking it to nothing.
            if (scale < 1) {
                row.style.setProperty('--nav-label-scale',
                    String(Math.max(LABEL_MIN_SCALE, Math.floor(scale * 100) / 100)));
            }
        };
        fit();
        // The column changes width on rotation; the word changes width when
        // the webfont arrives in place of the fallback it was measured in.
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(fit);
            ro.observe(row);
        }
        let live = true;
        document.fonts?.ready?.then(() => { if (live) fit(); });
        return () => { live = false; if (ro) ro.disconnect(); };
    }, [labelKey]);

    return (
        <nav className="bottom-nav-container" aria-label="Main">
          <div className="bottom-nav-row" ref={rowRef}>
            {itemsToRender.map((item, index) => {
                // While a tab is pressed it is THE active one. Not "also
                // active": several screens pass `active: true` for the tab
                // they are, and a bar showing two live tabs at once reads as
                // broken rather than as busy.
                const isActive = pressed != null ? pressed === index : activeTab === item.key;
                const isDisabled = item.needsSociety && !hasSocieties;

                const handleClick = () => {
                    setPressed(index);
                    if (isDisabled) {
                        navigate('/home');
                        return;
                    }
                    if (item.onClick) item.onClick();
                    else navigate(item.route, { state: item.state });
                };

                return (
                    <button
                        key={item.key}
                        type="button"
                        className={`bottom-nav-item ${isActive ? 'active' : ''}${isDisabled ? ' disabled' : ''}${item.live ? ' bottom-nav-item--live' : ''}`}
                        onClick={handleClick}
                        // The press begins before the tap completes, which on a
                        // phone buys the chunk a head start of the whole time a
                        // finger rests on the glass.
                        onPointerDown={() => preloadRoute(item.route)}
                        // The page you are on, for a screen reader. The visible
                        // label is the accessible name, so the icon is hidden
                        // from the tree rather than described twice.
                        aria-current={isActive ? 'page' : undefined}
                    >
                        <span className="bottom-nav-icon">
                            {renderGlyph(item, isActive)}
                            {item.live && <span className="bottom-nav-live-dot" aria-hidden="true" />}
                        </span>
                        <span className="bottom-nav-label">{item.name}</span>
                    </button>
                );
            })}
          </div>
        </nav>
    );
};

export default BottomNav;
